import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, COMMUNITY_PHOTOS } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { toast } from 'sonner';
import {
  Users,
  Calendar as CalendarIcon,
  MapPin,
  Clock,
  DollarSign,
  Heart,
  Megaphone,
  ClipboardList,
  Palette,
  Truck,
  CheckCircle,
  ExternalLink,
  RefreshCw
} from 'lucide-react';

const skillOptions = [
  { id: 'canvassing', label: 'Door-to-Door Canvassing', icon: Megaphone },
  { id: 'phone', label: 'Phone Banking', icon: ClipboardList },
  { id: 'social', label: 'Social Media', icon: Palette },
  { id: 'driving', label: 'Transportation/Driving', icon: Truck },
  { id: 'events', label: 'Event Planning', icon: CalendarIcon },
  { id: 'general', label: 'General Support', icon: Heart }
];

const GetInvolvedPage = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [volunteerCount, setVolunteerCount] = useState(0);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    skills: [],
    availability: ''
  });
  const [selectedDate, setSelectedDate] = useState(new Date());

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [eventsRes, countRes] = await Promise.all([
          axios.get(`${API}/events`),
          axios.get(`${API}/volunteers/count`)
        ]);
        setEvents(eventsRes.data);
        setVolunteerCount(countRes.data.count);
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleSkillToggle = (skillId) => {
    setFormData(prev => ({
      ...prev,
      skills: prev.skills.includes(skillId)
        ? prev.skills.filter(s => s !== skillId)
        : [...prev.skills, skillId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.phone || !formData.availability) {
      toast.error('Please fill in all required fields');
      return;
    }

    setFormSubmitting(true);
    try {
      await axios.post(`${API}/volunteers`, formData);
      toast.success('Thank you for volunteering! We\'ll be in touch soon.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        address: '',
        skills: [],
        availability: ''
      });
      setVolunteerCount(prev => prev + 1);
    } catch (error) {
      if (error.response?.data?.detail === 'Already signed up') {
        toast.error('This email has already been registered');
      } else {
        toast.error('Failed to submit. Please try again.');
      }
    } finally {
      setFormSubmitting(false);
    }
  };

  const eventTypeColors = {
    meeting: 'bg-blue-500',
    canvassing: 'bg-emerald-500',
    cleanup: 'bg-amber-500'
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 flex items-center gap-3">
            <Users className="w-10 h-10 text-purple-500" />
            The People's House
          </h1>
          <p className="mt-2 text-slate-600">
            Join the movement. Volunteer, attend events, and help build a better Killarney.
          </p>
        </div>

        {/* Stats Banner */}
        <Card className="bg-plp-blue text-white mb-8 overflow-hidden">
          <CardContent className="p-0">
            <div className="grid md:grid-cols-2">
              <img src={COMMUNITY_PHOTOS.group} alt="Killarney Community" className="h-48 md:h-auto w-full object-cover" />
              <div className="py-8 px-6 grid grid-cols-3 gap-4">
                <div className="text-center">
                  <p className="text-3xl md:text-4xl font-heading font-bold">{volunteerCount}</p>
                  <p className="text-white/80 text-sm">Volunteers Joined</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl md:text-4xl font-heading font-bold">{events.length}</p>
                  <p className="text-white/80 text-sm">Upcoming Events</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl md:text-4xl font-heading font-bold">100%</p>
                  <p className="text-white/80 text-sm">Community Driven</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Volunteer Form */}
          <div className="lg:col-span-2">
            <Card className="bg-white" data-testid="volunteer-form-card">
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <Heart className="w-5 h-5 text-red-500" />
                  Volunteer Sign-Up
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name">Full Name *</Label>
                      <Input
                        id="name"
                        placeholder="Your name"
                        value={formData.name}
                        onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                        data-testid="volunteer-name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="your@email.com"
                        value={formData.email}
                        onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                        data-testid="volunteer-email"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone">Phone *</Label>
                      <Input
                        id="phone"
                        placeholder="242-XXX-XXXX"
                        value={formData.phone}
                        onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                        data-testid="volunteer-phone"
                      />
                    </div>
                    <div>
                      <Label htmlFor="address">Address</Label>
                      <Input
                        id="address"
                        placeholder="Your address in Killarney"
                        value={formData.address}
                        onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                        data-testid="volunteer-address"
                      />
                    </div>
                  </div>

                  <div>
                    <Label>What can you help with?</Label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-2">
                      {skillOptions.map((skill) => (
                        <div
                          key={skill.id}
                          onClick={() => handleSkillToggle(skill.id)}
                          data-testid={`skill-${skill.id}`}
                          className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all min-h-[52px] ${
                            formData.skills.includes(skill.id)
                              ? 'border-plp-blue bg-plp-blue/5'
                              : 'border-slate-200 hover:border-slate-300'
                          }`}
                        >
                          <Checkbox
                            checked={formData.skills.includes(skill.id)}
                            className="pointer-events-none flex-shrink-0"
                          />
                          <skill.icon className="w-4 h-4 text-slate-500 flex-shrink-0" />
                          <span className="text-sm leading-tight">{skill.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="availability">Availability *</Label>
                    <Select
                      value={formData.availability}
                      onValueChange={(v) => setFormData(prev => ({ ...prev, availability: v }))}
                    >
                      <SelectTrigger data-testid="volunteer-availability">
                        <SelectValue placeholder="Select your availability" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="weekdays">Weekdays</SelectItem>
                        <SelectItem value="weekends">Weekends</SelectItem>
                        <SelectItem value="evenings">Evenings Only</SelectItem>
                        <SelectItem value="flexible">Flexible</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <Button
                    type="submit"
                    disabled={formSubmitting}
                    className="w-full bg-plp-blue hover:bg-plp-blue-light text-white py-6"
                    data-testid="volunteer-submit"
                  >
                    {formSubmitting ? (
                      <RefreshCw className="w-5 h-5 animate-spin mr-2" />
                    ) : (
                      <CheckCircle className="w-5 h-5 mr-2" />
                    )}
                    Join the Team
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Donate Card */}
            <Card className="bg-gradient-to-br from-plp-gold to-plp-gold-light" data-testid="donate-card">
              <CardContent className="p-6 text-center">
                <DollarSign className="w-12 h-12 text-plp-blue mx-auto mb-4" />
                <h3 className="font-heading font-bold text-xl text-plp-blue mb-2">Support the Campaign</h3>
                <p className="text-plp-blue/80 text-sm mb-4">
                  Your contribution helps us reach more residents and build a stronger Killarney.
                </p>
                <a
                  href="https://mysuncash.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block"
                >
                  <Button className="bg-plp-blue hover:bg-plp-blue-light text-white w-full" data-testid="donate-btn">
                    Donate via SunCash
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </Button>
                </a>
                <p className="text-xs text-plp-blue/60 mt-3">
                  Secure payments powered by SunCash Bahamas
                </p>
              </CardContent>
            </Card>

            {/* Upcoming Events */}
            <Card className="bg-white" data-testid="events-card">
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <CalendarIcon className="w-5 h-5 text-plp-blue" />
                  Upcoming Events
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {loading ? (
                  <div className="py-8 text-center">
                    <RefreshCw className="w-6 h-6 text-plp-blue animate-spin mx-auto" />
                  </div>
                ) : events.length === 0 ? (
                  <p className="text-center text-slate-500 py-4">No upcoming events</p>
                ) : (
                  events.slice(0, 4).map((event) => (
                    <div key={event.id} className="p-4 bg-slate-50 rounded-xl" data-testid={`event-${event.id}`}>
                      <div className="flex items-start gap-3">
                        <div className={`w-10 h-10 ${eventTypeColors[event.event_type] || 'bg-slate-500'} rounded-lg flex items-center justify-center flex-shrink-0`}>
                          <CalendarIcon className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1">
                          <Badge variant="outline" className="mb-1 text-xs capitalize">
                            {event.event_type}
                          </Badge>
                          <h4 className="font-heading font-semibold text-slate-900">{event.title}</h4>
                          <div className="mt-2 space-y-1 text-xs text-slate-500">
                            <p className="flex items-center gap-1">
                              <CalendarIcon className="w-3 h-3" />
                              {event.date}
                            </p>
                            <p className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {event.time}
                            </p>
                            <p className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {event.location}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>

            {/* Mini Calendar */}
            <Card className="bg-white">
              <CardHeader>
                <CardTitle className="font-heading text-lg">Event Calendar</CardTitle>
              </CardHeader>
              <CardContent>
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  className="rounded-md border w-full"
                />
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GetInvolvedPage;
